import AWS from 'aws-sdk';
import * as KVSWebRTC from 'amazon-kinesis-video-streams-webrtc';
import { isMobile, isIOS } from 'mobile-device-detect';
/* 아래 라이브러리가 import 되어야 사파리에서 webrtc가 동작함 */
import adapter from 'webrtc-adapter';
import { Crypto } from '@peculiar/webcrypto';

if (isIOS && isMobile) {
  const crypto = new Crypto();
  if (!window.crypto) window.crypto = crypto;
  else {
    // window.crypto.subtle이 수정 가능한지 확인
    try {
      window.crypto.subtle = window.crypto.subtle || crypto.subtle;
    } catch (e) {
      console.error("window.crypto.subtle is read-only. Unable to modify.", e);
    }
  }
}

// identityPoolId, roleARN을 이용한 경우
export default {
  /**
   * @description
   * Player : VideoJS로 실행할 Element,
   * Informs : {
   *   key: string,                     // USEN_WEBRTC_BUB23400009_LIVE_C1
   *   channelName: string,             // USEN_WEBRTC_BUB23400009_LIVE_C1
   *   clientId: string,                // String(Math.floor(Math.random() * 1000000))
   *   sendVideo: boolean,              // false
   *   sendAudio: boolean,              // false
   *   forceTURN: boolean,              // false
   *   wideScreen: boolean,             // true
   *   openDataChannel: boolean,        // false
   *   useTrickleICE: boolean,          // true
   *   natTraversalDisabled: boolean,   // false
   * },
   */
  viewers : new Map(),
  async startViewer(Player, Informs) {
    console.log(`[VIEWER] Informs : `);
    console.log(Informs);
    const viewer = {
      remoteView: null,   // UAC Player
      remoteStream: null, // UAC Stream
      localView: null,    // 매장내 PC Player
      localStream: null,  // 매장내 PC Stream
      signalingClient: null,
      dataChannel: null,
      peerConnection: null,
      peerConnectionStatsInterval: null,
    };

    // kvs webrtc 연결 로직 시작
    viewer.remoteView = Player; // this.$refs.videoElement || videojs('my-video');
    const kinesisVideoClient = new AWS.KinesisVideo({
      correctClockSkew: true,
    });
    console.log(`[VIEWER] kinesisVideoClient : ${kinesisVideoClient}`);

    const describeSignalingChannelResponse = await kinesisVideoClient
      .describeSignalingChannel({
        ChannelName: Informs.channelName,
      })
      .promise();
    const channelARN = describeSignalingChannelResponse.ChannelInfo.ChannelARN;
    console.log(`[VIEWER] Channel ARN: ${channelARN}`);

    const getSignalingChannelEndpointResponse = await kinesisVideoClient
      .getSignalingChannelEndpoint({
        ChannelARN: channelARN,
        SingleMasterChannelEndpointConfiguration: {
          Protocols: ['WSS', 'HTTPS'],
          Role: KVSWebRTC.Role.VIEWER,
        },
      })
      .promise();

    const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
      endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
      return endpoints;
    }, {});
    console.log(`[VIEWER] Endpoints: ${JSON.stringify(endpointsByProtocol)}`);

    const kinesisVideoSignalingChannelsClient = new AWS.KinesisVideoSignalingChannels({
      endpoint: endpointsByProtocol.HTTPS,
      correctClockSkew: true,
    });
    const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
      .getIceServerConfig({
        ChannelARN: channelARN,
      })
      .promise();
    const iceServers = [];
    if (!Informs.natTraversalDisabled && !Informs.forceTURN) {
      iceServers.push({ urls: `stun:stun.kinesisvideo.${AWS.config.region}.amazonaws.com:443` });
    }
    if (!Informs.natTraversalDisabled) {
      getIceServerConfigResponse.IceServerList.forEach(iceServer => {
        iceServers.push({
          urls: iceServer.Uris,
          username: iceServer.Username,
          credential: iceServer.Password,
        });
      });
    }
    console.log(`[VIEWER] ICE servers: ${JSON.stringify(iceServers)}`);

    viewer.signalingClient = new KVSWebRTC.SignalingClient({
      channelARN,
      channelEndpoint: endpointsByProtocol.WSS,
      clientId: Informs.clientId,
      role: KVSWebRTC.Role.VIEWER,
      region: AWS.config.region,
      credentials: AWS.config.credentials,
      systemClockOffset: kinesisVideoClient.config.systemClockOffset,
    });

    console.log(`[VIEWER] signaling Client : ${viewer.signalingClient}`);

    const resolution = Informs.widescreen ? { width: { ideal: 1280 }, height: { ideal: 720 } } : { width: { ideal: 640 }, height: { ideal: 480 } };
    const constraints = {
      video: Informs.sendVideo ? resolution : false,
      ...Informs.sendAudio && {audio: { echoCancellation: true }},
    };
    
    const configuration = {
      iceServers,
      // relay면 TURN 서버만 사용하게 됨
      // all 이면 STUN 서버를 우선적으로 사용하고, 실패하면 TURN 서버를 사용하게 됨
      iceTransportPolicy: Informs.forceTURN ? 'relay' : 'all',
    };
    viewer.peerConnection = new RTCPeerConnection(configuration);
    if (Informs.openDataChannel) {
      const dataChannelObj = viewer.peerConnection.createDataChannel('kvsDataChannel');
      viewer.dataChannel = dataChannelObj;
    }

    viewer.signalingClient.on('open', async () => {
      console.log(`[VIEWER] Connected to signaling service`);
      if (Informs.sendVideo || Informs.sendAUdio) {
        try {
          viewer.localStream = await navigator.mediaDevices.getUserMedia(constraints);
          viewer.localStream.getTracks().forEach((track) => {
            viewer.peerConnection.addTrack(track, viewer.localStream);
          })
        } catch (e) {
          console.error(`[VIEWER] Could not find webcam`);
          return;
        }
      }

      console.log(`[VIEWER] Creating SDP offer`);
      await viewer.peerConnection.setLocalDescription(
        await viewer.peerConnection.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        }),
      );
      if (Informs.useTrickleICE) {
        console.log(`[VIEWER] Sending SDP offer`);
        viewer.signalingClient.sendSdpOffer(viewer.peerConnection.localDescription);
      }


      console.log(`[VIEWER] Generating ICE candidates`);
    });

    viewer.signalingClient.on('sdpAnswer', async (answer) => {
      console.log(`[VIEWER] Received SDP answer`);
      await viewer.peerConnection.setRemoteDescription(answer);
    });

    viewer.signalingClient.on('iceCandidate', (candidate) => {
      console.log(`[VIEWER] Received ICE candidate`);
      viewer.peerConnection.addIceCandidate(candidate);
    });

    viewer.signalingClient.on('close', () => {
      console.log(`[VIEWER] Disconnected from signaling channel`);
      this.logPeerConnectionState(viewer.peerConnection);
    });

    viewer.signalingClient.on('error', (error) => {
      console.error(`[VIEWER] Signaling client error: ${error}`);
      console.log(error);
    });

    viewer.peerConnection.addEventListener('icecandidate', ({candidate}) => {
      if (candidate) {
        console.log(`[VIEWER] Generated ICE candidate: ${JSON.stringify(candidate)}`);
        if (candidate.candidate.includes('relay')) {
          console.log(`[VIEWER] This candidate is using TURN server`);
        } else if (candidate.candidate.includes('srflx')) {
          console.log(`[VIEWER] This candidate is using STUN server`);
        } else if (candidate.candidate.includes('host')) {
          console.log(`[VIEWER] This candidate is a host candidate`);
        }
        if (Informs.useTrickleICE) {
          console.log(`[VIEWER] Sending ICE candidate`);
          viewer.signalingClient.sendIceCandidate(candidate);
        }
      } else {
        console.log(`[VIEWER] All ICE candidates have been generated`);
        if (!Informs.useTrickleICE) {
          console.log(`[VIEWER] Sending SDP offer`);
          viewer.signalingClient.sendSdpOffer(viewer.peerConnection.localDescription);
        }
      }
    });

    // 실제 데이터 전송이 가능한지 데이터채널 통신 가능성을 파악할 수 있음.
    viewer.peerConnection.onconnectionstatechange = (evt) => {
      console.warn('viewer peerConnection', viewer.peerConnection.connectionState, evt);
    }

    // P2P의 전반적인 상태를 나타내며 네트워크 연결의 안정성을 모니터링 할 수 있음.
    // ICE 연결 상태가 변경될 때마다 사용된 후보 정보를 로그 기록
    viewer.peerConnection.oniceconnectionstatechange = async (event) => {
      const rtcPeerConnection = event.target;
      console.log(`[VIEWER] ICE connection state changed to ${rtcPeerConnection.iceConnectionState}`);

      if (rtcPeerConnection.iceConnectionState === 'connected') {
        console.log(`[VIEWER] ICE connection state is connected`);

        // 실제로 연결된 ICE 후보 쌍을 로그로 출력
        const stats = await viewer.peerConnection.getStats(null);
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            console.log(`[VIEWER] Connected using local candidate: ${report.localCandidateId}`);
            console.log(`[VIEWER] Connected using remote candidate: ${report.remoteCandidateId}`);
            stats.forEach(candidate => {
              if (candidate.id === report.localCandidateId) {
                console.log(`[VIEWER] Local candidate details: ${JSON.stringify(candidate)}`);
              } else if (candidate.id === report.remoteCandidateId) {
                console.log(`[VIEWER] Remote candidate details: ${JSON.stringify(candidate)}`);
              }
            });
          }
        });
      } else if (rtcPeerConnection.iceConnectionState === 'disconnected') {
        console.log(`[VIEWER] ICE connection state is disconnected`);
        // 추가: disconnected 상태의 사유를 로그로 기록
        const stats = await viewer.peerConnection.getStats(null);
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'failed') {
            console.log(`[VIEWER] Failed candidate pair: ${JSON.stringify(report)}`);
          } else if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
            console.log(`[VIEWER] Candidate details: ${JSON.stringify(report)}`);
          }
        });
      } else if (rtcPeerConnection.iceConnectionState === 'failed') {
        console.log(`[VIEWER] ICE connection state is failed`);
        // 실패 원인을 로그로 기록
        const stats = await viewer.peerConnection.getStats(null);
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'failed') {
            console.log(`[VIEWER] Failed candidate pair: ${JSON.stringify(report)}`);
          } else if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
            console.log(`[VIEWER] Candidate details: ${JSON.stringify(report)}`);
          }
        });
        // TODO: stop 되면 그냥 까만 화면으로 멈춰 있는데.. 이후에는 어떻게 할지 고민해봐야 함
        this.stopViewer(Informs);
      }
    };

    // // ICE 후보 수집 완료를 알리는 Promise 생성
    const iceCandidatesCollected = new Promise((resolve, reject) => {
      viewer.peerConnection.onicecandidate = ({ candidate }) => {
        if (!candidate) {
          // 모든 후보가 생성되었을 때
          resolve();
        }
      };
    });

    viewer.peerConnection.ontrack = event => {
      console.log(event);
      viewer.remoteStream = new MediaStream();
      viewer.remoteView.srcObject = viewer.remoteStream;
      viewer.remoteView.style.display = 'block';
      event.streams[0].getTracks().forEach(track => {
        console.log(track)
        viewer.remoteStream.addTrack(track);
      });
      console.log(viewer.remoteView.srcObject);

      // 추가: playoutDelayHint 설정
      // 상암동 요청으로 삭제
      // event.receiver.playoutDelayHint = 0.1; // 원하는 지연 시간(초)을 설정합니다.
    }

    viewer.signalingClient.open();
    // kvs webrtc 연결 로직 종료
    this.viewers.set(Informs.key, viewer);
    
    // ICE 후보 수집이 완료될 때까지 기다림
    await iceCandidatesCollected;
    
    return;
  },
  logPeerConnectionState(peerConnection) {
    if (!peerConnection) return;
    console.log(`[VIEWER] PeerConnection state: ${peerConnection.connectionState}`);
    peerConnection.getStats().then(stats => {
      stats.forEach(report => {
        console.log(`[VIEWER] Report type: ${report.type}`);
        console.log(report);
      });
    }).catch(err => {
      console.error(`[VIEWER] Error getting stats: ${err}`);
    });
  },
  stopViewer(Informs) {
    const viewer = this.viewers.get(Informs.key);
    if (!viewer) {
      return;
    }
    if (viewer.peerConnectionStatsInterval) {
      clearInterval(viewer.peerConnectionStatsInterval);
      viewer.peerConnectionStatsInterval = null;
    }
    if (viewer.dataChannel) {
      viewer.dataChannel.close();
      viewer.dataChannel = null;
    }
    if (viewer.signalingClient) {
      viewer.signalingClient.close();
      viewer.signalingClient = null;
    }
    if (viewer.peerConnection) {
      viewer.peerConnection.close();
      viewer.peerConnection = null;
    }
    if (viewer.localView) {
      viewer.localView.srcObject = null;
    }
    if (viewer.localStream) {
      viewer.localStream.getTracks().forEach(track => track.stop());
      viewer.localStream = null;
    }
    if (viewer.remoteView) {
      viewer.remoteView.srcObject = null;
    }
    if (viewer.remoteStream) {
      viewer.remoteStream.getTracks().forEach(track => track.stop());
      viewer.remoteStream = null;
    }
    this.viewers.delete(Informs.key);
  },
  async removeRTCRemnants() {
    return new Promise((resolve) => {
      const keys = this.viewers.keys();
      for (const key of keys) this.stopViewer({ key : key });
      if (this.viewers.size < 1) resolve(true);
    })
  },
} 